import React, { useState, useEffect, useLayoutEffect, useRef, forwardRef, useMemo, useImperativeHandle } from 'react';
import {
  DndContext, DragOverlay, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, defaultAnimateLayoutChanges,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getProfil, rebuildDokument, updateStelle } from '../api';
import { getTemplate, inferTemplateId } from '../templates';
import { normalisiereCvMarkierungen } from '../utils/cvHighlighter';
import FotoPickerModal from './FotoPickerModal';
import FotoCropperModal from './FotoCropperModal';

const UndoIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.92 8.97957H8.77L9.1 8.64957C9.39 8.35957 9.39 7.87957 9.1 7.58957C8.81 7.29957 8.33 7.29957 8.04 7.58957L6.47 9.15957C6.18 9.44957 6.18 9.92957 6.47 10.2196L8.04 11.7896C8.19 11.9396 8.38 12.0096 8.57 12.0096C8.76 12.0096 8.95 11.9396 9.1 11.7896C9.39 11.4996 9.39 11.0196 9.1 10.7296L8.84 10.4696H13.92C15.2 10.4696 16.25 11.5096 16.25 12.7996C16.25 14.0896 15.21 15.1296 13.92 15.1296H9C8.59 15.1296 8.25 15.4696 8.25 15.8796C8.25 16.2896 8.59 16.6296 9 16.6296H13.92C16.03 16.6296 17.75 14.9096 17.75 12.7996C17.75 10.6896 16.03 8.97957 13.92 8.97957Z"/>
  </svg>
);

const RedoIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.53 9.15957L15.96 7.58957C15.67 7.29957 15.19 7.29957 14.9 7.58957C14.61 7.87957 14.61 8.35957 14.9 8.64957L15.23 8.97957H10.08C7.97 8.97957 6.25 10.6996 6.25 12.8096C6.25 14.9196 7.97 16.6396 10.08 16.6396H15C15.41 16.6396 15.75 16.2996 15.75 15.8896C15.75 15.4796 15.41 15.1396 15 15.1396H10.08C8.8 15.1396 7.75 14.0996 7.75 12.8096C7.75 11.5196 8.79 10.4796 10.08 10.4796H15.16L14.9 10.7396C14.61 11.0296 14.61 11.5096 14.9 11.7996C15.05 11.9496 15.24 12.0196 15.43 12.0196C15.62 12.0196 15.81 11.9496 15.96 11.7996L17.53 10.2296C17.82 9.92957 17.82 9.45957 17.53 9.15957Z"/>
  </svg>
);

// ── Parser ────────────────────────────────────────────────────

/**
 * Konvertiert das neue strukturierte cv_data (dict aus Firestore) in dasselbe
 * Format das parseRohtext() zurückgibt — inkl. inhaltRaw für Edit-Kompatibilität.
 */
function cvDataZuParseResult(cvData) {
  const sektionen = (cvData.sektionen || []).map((s) => {
    const lines = [];
    if (s.typ === 'eintraege') {
      for (const e of (s.eintraege || [])) {
        lines.push(`DATUM: ${e.datum}`);
        lines.push(`JOBTITEL: ${e.jobtitel}`);
        lines.push(`ARBEITGEBER: ${e.arbeitgeber}`);
        for (const b of (e.bullets || [])) lines.push(`BULLET: ${b}`);
      }
    } else if (s.typ === 'kenntnisse') {
      for (const k of (s.kenntnisse || [])) {
        if (k.kategorie) lines.push(`KKATEGORIE: ${k.kategorie}`);
        lines.push(`KTITEL: ${k.titel}`);
        lines.push(`KBESCHR: ${k.beschreibung}`);
      }
    } else {
      for (const b of (s.bullets || [])) lines.push(`BULLET: ${b}`);
    }
    return { ...s, inhaltRaw: lines.join('\n') };
  });
  return {
    profil: cvData.profil_text || '',
    sektionen,
    keywordMarkierungen: cvData.keyword_markierungen || [],
  };
}

/** Gibt {profil, sektionen} zurück — cv_data bevorzugt, cv_rohtext als Fallback. */
function parseCvSource(stelle) {
  if (stelle?.cv_data) return cvDataZuParseResult(stelle.cv_data);
  if (stelle?.cv_rohtext) return parseRohtext(stelle.cv_rohtext);
  return { profil: '', sektionen: [], keywordMarkierungen: [] };
}

function parseRohtext(rohtext) {
  const text = rohtext.replace(/\s*---\s*$/, '').trim();
  const teile = text.split(/\n?## /);

  const profilTeil = teile[0].trim();
  const profil = profilTeil.startsWith('PROFIL:')
    ? profilTeil.slice(7).trim()
    : profilTeil;

  const sektionen = teile.slice(1).map((teil) => {
    const zeilen = teil.split('\n');
    const name = zeilen[0].trim();
    const inhaltZeilen = zeilen.slice(1).map((z) => z.trim()).filter(Boolean);
    const inhaltRaw = zeilen.slice(1).join('\n').trim();
    return { name, inhaltRaw, ...parseSektionInhalt(inhaltZeilen) };
  });

  return { profil, sektionen, keywordMarkierungen: [] };
}

function parseSektionInhalt(zeilen) {
  if (zeilen.some((z) => z.startsWith('KTITEL:'))) {
    const kenntnisse = [];
    let current = null;
    let aktuelleKategorie = '';
    for (const z of zeilen) {
      if (z.startsWith('KKATEGORIE:')) {
        aktuelleKategorie = z.slice(11).trim();
        if (current) current.kategorie = aktuelleKategorie;
      } else if (z.startsWith('KTITEL:')) {
        current = { kategorie: aktuelleKategorie, titel: z.slice(7).trim(), beschreibung: '' };
        kenntnisse.push(current);
      } else if (z.startsWith('KBESCHR:') && current) {
        current.beschreibung = z.slice(8).trim();
      }
    }
    return { typ: 'kenntnisse', kenntnisse };
  }

  const hatFirma = zeilen.some((z) => z.startsWith('FIRMA:'));
  const hatEintrag =
    hatFirma ||
    zeilen.some((z) => z.startsWith('DATUM:') || z.startsWith('JOBTITEL:'));

  if (hatEintrag) {
    const eintraege = [];
    let current = null;

    for (const z of zeilen) {
      if (hatFirma && z.startsWith('FIRMA:')) {
        current = { firma: z.slice(6).trim(), datum: '', rolle: '', bullets: [] };
        eintraege.push(current);
      } else if (!hatFirma && z.startsWith('DATUM:')) {
        current = { datum: z.slice(6).trim(), jobtitel: '', arbeitgeber: '', bullets: [] };
        eintraege.push(current);
      } else if (current) {
        if (z.startsWith('DATUM:')) current.datum = z.slice(6).trim();
        else if (z.startsWith('ROLLE:')) current.rolle = z.slice(6).trim();
        else if (z.startsWith('JOBTITEL:')) current.jobtitel = z.slice(9).trim();
        else if (z.startsWith('ARBEITGEBER:')) current.arbeitgeber = z.slice(12).trim();
        else if (z.startsWith('BULLET:')) current.bullets.push(z.slice(7).trim());
      }
    }
    return { typ: 'eintraege', eintraege };
  }

  const bullets = zeilen
    .filter((z) => z.startsWith('BULLET:'))
    .map((z) => z.slice(7).trim());
  return { typ: 'bullets', bullets };
}

function sektionKey(s) {
  return (s?.name || 'sektion')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'sektion';
}

// ── Sub-Komponenten CV Modern ─────────────────────────────────

const _kurzUrl = url => (url || '').replace(/^https?:\/\/(www\.)?/, '');

function ModernKopf({ profil, nutzer }) {
  const vollName = `${nutzer?.vorname || ''} ${nutzer?.nachname || ''}`.trim() || nutzer?.name || '';
  const adresse = nutzer?.anschrift || [nutzer?.strasse, nutzer?.hausnummer, nutzer?.plz, nutzer?.ort].filter(Boolean).join(' ');
  const kontakt = [adresse, nutzer?.telefon, nutzer?.email].filter(Boolean).join(' | ');
  const links = [
    nutzer?.linkedin  ? `LinkedIn: ${_kurzUrl(nutzer.linkedin)}`  : null,
    nutzer?.portfolio ? `Website: ${_kurzUrl(nutzer.portfolio)}`  : null,
    nutzer?.github    ? `GitHub: ${_kurzUrl(nutzer.github)}`      : null,
  ].filter(Boolean).join(' | ');
  return (
    <div className="cv-m-kopf">
      <h1 className="cv-m-name">{vollName.toUpperCase()}</h1>
      <p className="cv-m-kontakt">{kontakt}</p>
      {links && <p className="cv-m-kontakt">{links}</p>}
      <hr className="cv-m-linie" />
      <p className="cv-m-profil">{profil}</p>
      <hr className="cv-m-linie" />
    </div>
  );
}

function ModernSektion({ s, sektionIdx, zeigeTitel = true, vonEintrag = 0, bisEintrag }) {
  const eintraege = s.typ === 'eintraege'
    ? s.eintraege.slice(vonEintrag, bisEintrag)
    : [];

  return (
    <div className="cv-m-sektion">
      {zeigeTitel && (
        <h2 className="cv-m-sektion-titel"
            {...(sektionIdx !== undefined ? { 'data-sektion-titel': sektionIdx } : {})}>
          {s.name.toUpperCase()}
        </h2>
      )}
      {s.typ === 'kenntnisse' && (
        <div className="cv-m-kenntnisse-grid">
          {s.kenntnisse.map((k, i) => (
            <div key={i} className="cv-m-kenntnis">
              <div className="cv-m-kenntnis-titel">{k.titel.toUpperCase()}</div>
              <div className="cv-m-kenntnis-beschr">{k.beschreibung}</div>
            </div>
          ))}
        </div>
      )}
      {s.typ === 'eintraege' && (
        <div className="cv-m-eintraege-liste">
          {eintraege.map((e, localIdx) => {
            const globalIdx = vonEintrag + localIdx;
            return (
              <div key={globalIdx} className="cv-m-eintrag"
                   {...(sektionIdx !== undefined ? { 'data-eintrag': `${sektionIdx}-${globalIdx}` } : {})}>
                <div className="cv-m-eintrag-kopf">
                  <span className="cv-m-firma">
                    {(() => {
                      const firmaText = e.firma || e.arbeitgeber;
                      if (!firmaText) return null;
                      const [name, ...rest] = firmaText.split('|').map(s => s.trim());
                      return rest.length > 0
                        ? <>{name.toUpperCase()},&nbsp;<span style={{fontWeight: 400}}>{rest.join(', ').toUpperCase()}</span></>
                        : name.toUpperCase();
                    })()}
                  </span>
                  {e.datum && <span className="cv-m-datum">{e.datum}</span>}
                </div>
                {(e.rolle || e.jobtitel) && <div className="cv-m-rolle">{e.rolle || e.jobtitel}</div>}
                {(e.bullets?.length > 0) && (
                  <ul className="cv-m-bullets">
                    {e.bullets.map((b, j) => <li key={j}>{b}</li>)}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
      {s.typ === 'bullets' && (
        <ul className="cv-m-bullets">
          {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
    </div>
  );
}

function ModernFooter({ nutzer }) {
  const vollName = `${nutzer?.vorname || ''} ${nutzer?.nachname || ''}`.trim() || nutzer?.name || '';
  const adresse = nutzer?.anschrift || [nutzer?.strasse, nutzer?.hausnummer, nutzer?.plz, nutzer?.ort].filter(Boolean).join(' ');
  return (
    <div className="cv-m-footer">
      {[vollName, adresse, nutzer?.telefon, nutzer?.email]
        .filter(Boolean).join(' | ')}
    </div>
  );
}

// ── Sub-Komponenten CV PsyFix ─────────────────────────────────

function PsyFixKopf({ profil, nutzer }) {
  const vollName = `${nutzer?.vorname || ''} ${nutzer?.nachname || ''}`.trim() || nutzer?.name || '';
  const adresse = nutzer?.anschrift || [nutzer?.strasse, nutzer?.hausnummer, nutzer?.plz, nutzer?.ort].filter(Boolean).join(' ');
  const kontakt = [adresse, nutzer?.telefon, nutzer?.email].filter(Boolean).join(' | ');
  const links = [
    nutzer?.linkedin  ? `LinkedIn: ${_kurzUrl(nutzer.linkedin)}`  : null,
    nutzer?.portfolio ? `Website: ${_kurzUrl(nutzer.portfolio)}`  : null,
    nutzer?.github    ? `GitHub: ${_kurzUrl(nutzer.github)}`      : null,
  ].filter(Boolean).join(' | ');
  return (
    <div className="cv-p-kopf">
      <h1 className="cv-p-name">{vollName}</h1>
      <p className="cv-p-kontakt">{kontakt}</p>
      {links && <p className="cv-p-kontakt">{links}</p>}
      <hr className="cv-p-linie" />
      <p className="cv-p-profil">{profil}</p>
      <hr className="cv-p-linie" />
    </div>
  );
}

function PsyFixSektion({ s, sektionIdx, zeigeTitel = true, vonEintrag = 0, bisEintrag }) {
  const eintraege = s.typ === 'eintraege'
    ? s.eintraege.slice(vonEintrag, bisEintrag)
    : [];

  return (
    <div className="cv-p-sektion">
      {zeigeTitel && (
        <h2 className="cv-p-sektion-titel"
            {...(sektionIdx !== undefined ? { 'data-sektion-titel': sektionIdx } : {})}>
          {s.name}
        </h2>
      )}
      {s.typ === 'eintraege' && eintraege.map((e, localIdx) => {
        const globalIdx = vonEintrag + localIdx;
        return (
          <div key={globalIdx} className="cv-p-eintrag"
               {...(sektionIdx !== undefined ? { 'data-eintrag': `${sektionIdx}-${globalIdx}` } : {})}>
            {e.datum && <div className="cv-p-datum">{e.datum}</div>}
            {(e.jobtitel || e.arbeitgeber) && (
              <div className="cv-p-titel">
                {e.jobtitel && <span className="cv-p-titel-job">{e.jobtitel}</span>}
                {e.jobtitel && e.arbeitgeber && <span className="cv-p-titel-sep"> / </span>}
                {e.arbeitgeber && <span className="cv-p-titel-arbeitgeber">{e.arbeitgeber}</span>}
              </div>
            )}
            {e.firma && <div className="cv-p-titel">{e.firma}</div>}
            {e.rolle && <div className="cv-p-rolle">{e.rolle}</div>}
            {(e.bullets?.length > 0) && (
              <ul className="cv-p-bullets">
                {e.bullets.map((b, j) => <li key={j}>{b}</li>)}
              </ul>
            )}
          </div>
        );
      })}
      {s.typ === 'bullets' && (
        <ul className="cv-p-bullets">
          {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
    </div>
  );
}

function PsyFixFooter({ nutzer }) {
  const vollName = `${nutzer?.vorname || ''} ${nutzer?.nachname || ''}`.trim() || nutzer?.name || '';
  const adresse = nutzer?.anschrift || [nutzer?.strasse, nutzer?.hausnummer, nutzer?.plz, nutzer?.ort].filter(Boolean).join(' ');
  return (
    <div className="cv-p-footer">
      {[vollName, adresse, nutzer?.telefon, nutzer?.email]
        .filter(Boolean).join(' | ')}
    </div>
  );
}

// ── Drag Wrapper ──────────────────────────────────────────────

const stableDropAnimation = (args) => {
  if (args.wasDragging) return false;
  return defaultAnimateLayoutChanges(args);
};

function SortableSektionWrapper({ sId, children, onEditClick, isMobile }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sId,
    animateLayoutChanges: stableDropAnimation,
  });

  if (isMobile) {
    return (
      <div
        ref={setNodeRef}
        style={{ transform: CSS.Translate.toString(transform), transition }}
        className={`cv-sektion-sortable is-mobile${isDragging ? ' is-dragging' : ''}`}
        {...attributes}
        {...listeners}
        onClick={onEditClick}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`cv-sektion-sortable${isDragging ? ' is-dragging' : ''}`}
      onClick={onEditClick}
    >
      {/* ponytail: Verschieben-Griff direkt; Klick darauf nicht zum Bearbeiten durchreichen */}
      <div
        className="cv-drag-handle-btn"
        {...attributes}
        {...listeners}
        title="Sektion verschieben"
        onClick={(e) => e.stopPropagation()}
      >⠿</div>
      {children}
    </div>
  );
}

// ── Seiten-Berechnung ─────────────────────────────────────────

const PAGE_CONTENT_HEIGHT = 971; // 297mm - 20mm top - 20mm bottom @ 96dpi
function footerReserved(_tid) { return 0; }
// .cv-pf-kopf bleeds 20mm upward into the top page margin (margin-top: -20mm),
// so that much of its offsetHeight already overlaps space PAGE_CONTENT_HEIGHT
// excluded — without this, that 20mm gets subtracted twice and pages break early.
const PROFESSIONELL_HEADER_BLEED = (20 / 25.4) * 96;

function berechneSeitenverteilung(measureEl, sektionen, templateId = 'modern') {
  // Reale Bausteinhöhen aus echter Geometrie: Der Mess-Container legt jeden Baustein
  // fortlaufend mit seinen echten CSS-Margins aus, und alle Bausteine haben denselben
  // offsetParent — daher ist (nächster.offsetTop − dieser.offsetTop) exakt der Platz,
  // den ein Baustein inkl. Abstand zum nächsten verbraucht. Das ersetzt die alten festen
  // Aufschläge +16/+18/+10/+16, die den Verbrauch überschätzten und den letzten Abschnitt
  // auf eine überzählige, fast leere Seite drängten.
  const kopfEl = measureEl.querySelector('[data-role="kopf"]');
  const units = [];
  if (kopfEl) units.push({ key: 'kopf', el: kopfEl });
  for (let i = 0; i < sektionen.length; i++) {
    const s = sektionen[i];
    if (s.typ === 'kenntnisse' || s.typ === 'bullets') {
      const el = measureEl.querySelector(`[data-sektion="${i}"]`);
      if (el) units.push({ key: `sektion-${i}`, el });
    } else if (s.typ === 'eintraege') {
      const titelEl = measureEl.querySelector(`[data-sektion-titel="${i}"]`);
      if (titelEl) units.push({ key: `titel-${i}`, el: titelEl });
      for (let j = 0; j < s.eintraege.length; j++) {
        const eintragEl = measureEl.querySelector(`[data-eintrag="${i}-${j}"]`);
        if (eintragEl) units.push({ key: `eintrag-${i}-${j}`, el: eintragEl });
      }
    }
  }
  const hoehe = {};
  for (let u = 0; u < units.length; u++) {
    const top = units[u].el.offsetTop;
    const next = u + 1 < units.length
      ? units[u + 1].el.offsetTop
      : top + units[u].el.offsetHeight;
    hoehe[units[u].key] = Math.max(0, next - top);
  }

  const headerH = kopfEl ? hoehe['kopf'] : 150;
  const headerBleed = templateId === 'professionell' ? PROFESSIONELL_HEADER_BLEED : 0;
  const available = PAGE_CONTENT_HEIGHT - footerReserved(templateId);

  const pages = [];
  let aktuelleSeite = { hatKopf: true, teile: [], verbleibend: available - headerH + headerBleed };

  function neueSeite() {
    pages.push(aktuelleSeite);
    aktuelleSeite = { hatKopf: false, teile: [], verbleibend: available };
  }

  for (let i = 0; i < sektionen.length; i++) {
    const s = sektionen[i];

    if (s.typ === 'kenntnisse' || s.typ === 'bullets') {
      const h = hoehe[`sektion-${i}`] ?? 80;
      if (h > aktuelleSeite.verbleibend && aktuelleSeite.teile.length > 0) {
        neueSeite();
      }
      aktuelleSeite.teile.push({ sektionIdx: i, zeigeTitel: true, vonEintrag: 0, bisEintrag: s.bullets?.length ?? 0 });
      aktuelleSeite.verbleibend -= h;

    } else if (s.typ === 'eintraege') {
      const titelH = hoehe[`titel-${i}`] ?? 25;

      if (titelH > aktuelleSeite.verbleibend && aktuelleSeite.teile.length > 0) {
        neueSeite();
      }
      aktuelleSeite.verbleibend -= titelH;

      let vonEintrag = 0;
      let zeigeTitel = true;

      for (let j = 0; j < s.eintraege.length; j++) {
        const eintragH = hoehe[`eintrag-${i}-${j}`] ?? 50;

        if (eintragH > aktuelleSeite.verbleibend) {
          if (j === vonEintrag) {
            // Erster Eintrag passt nicht — Titel zurückgeben, auf neuer Seite neu starten
            aktuelleSeite.verbleibend += titelH;
            neueSeite();
            aktuelleSeite.verbleibend -= titelH;
          } else {
            aktuelleSeite.teile.push({ sektionIdx: i, zeigeTitel, vonEintrag, bisEintrag: j });
            neueSeite();
            vonEintrag = j;
            zeigeTitel = false;
          }
        }
        aktuelleSeite.verbleibend -= eintragH;
      }

      aktuelleSeite.teile.push({ sektionIdx: i, zeigeTitel, vonEintrag, bisEintrag: s.eintraege.length });
    }
  }
  pages.push(aktuelleSeite);
  return pages;
}

// ── Haupt-Component ───────────────────────────────────────────

const CV_SKEL_KW_POSITIONS = [
  { x: 14, y: 18, d: '0s' }, { x: 63, y: 16, d: '0.16s' }, { x: 38, y: 28, d: '0.32s' },
  { x: 18, y: 43, d: '0.48s' }, { x: 68, y: 42, d: '0.64s' }, { x: 43, y: 54, d: '0.8s' },
  { x: 17, y: 69, d: '0.96s' }, { x: 61, y: 68, d: '1.12s' }, { x: 35, y: 80, d: '1.28s' },
  { x: 72, y: 84, d: '1.44s' },
];

// Maße einer A4-Seite in Bildschirmpunkten (210 × 297 mm bei 96 dpi) — dieselben Zahlen,
// die `.cv-pagina` im Stylesheet als `210mm`/`297mm` stehen hat.
const CV_SEITE_BREITE = 794;
const CV_SEITE_HOEHE = 1123;
// Seitlicher Rand um die Seite herum, damit sie nicht an den Spaltenkanten klebt.
const CV_RAND_X = 72;
// Ein Fingerbreit Luft unter der Seite. Ohne ihn bliebe ein Streifen übrig, und ein
// Streifen genügt, damit der Balken zum Scrollen wieder auftaucht.
const CV_LUFT = 8;
// Untergrenze der Verkleinerung, gleicher Wert wie beim Anschreiben.
const CV_SKALA_MIN = 0.45;

const CVVorschau = forwardRef(function CVVorschau({ stelle, dokumentOptionen, onRebuildDone, isMobile, onEditToggle, onProfilClick, editSektionIdx = null, onUndo, onRedo, canUndo, canRedo, undoCount = 0, redoCount = 0, onFotoChange, tabsElement, loading, keywords, bearbeitenButton, bearbeitungsmodus = false, istLeererEntwurf = false, entwurfProfilName = null, zoom = null, onFit }, ref) {
  const [nutzer, setNutzer] = useState(null);
  const [entwurfSnapshot, setEntwurfSnapshot] = useState(null);
  const [pages, setPages] = useState(null);
  const [templateId, setTemplateId] = useState('standard');
  const measureRef = useRef(null);
  const containerRef = useRef(null);
  // Forward the external ref to the same DOM element as containerRef
  useImperativeHandle(ref, () => containerRef.current);
  const [fotoPickerOffen, setFotoPickerOffen] = useState(false);
  const [highlightingAktiv, setHighlightingAktiv] = useState(false);
  const [cropperBild, setCropperBild] = useState(null);
  const [cropperNeuesFile, setCropperNeuesFile] = useState(null);
  const [aktiveDragId, setAktiveDragId] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  const istLebenslaufEntwurf = stelle?.quelle === 'lebenslauf';

  useEffect(() => {
    if (istLebenslaufEntwurf) return; // Lebenslauf-Entwürfe frieren ihre Stammdaten separat ein (siehe unten)
    if (!stelle?.profil_name) return;
    getProfil(stelle.profil_name)
      .then((r) => setNutzer(r.data))
      .catch(() => {});
  }, [stelle?.profil_name, istLebenslaufEntwurf]);

  // Lebenslauf-Entwürfe haben kein eigenes profil_name und sind nicht an ein Profil
  // gebunden — beim ersten Öffnen wird eine feste Kopie der Stammdaten in absender_*
  // geschrieben, damit spätere Profil-Bearbeitungen diesen Entwurf nicht mehr verändern.
  useEffect(() => {
    if (!istLebenslaufEntwurf) return;
    if (stelle?.absender_name) return; // schon eingefroren
    if (!entwurfProfilName || !stelle?.id) return;
    let abgebrochen = false;
    getProfil(entwurfProfilName).then((r) => {
      if (abgebrochen) return;
      const p = r.data || {};
      const name = [p.vorname, p.nachname].filter(Boolean).join(' ').trim();
      const strasseZeile = [p.strasse, p.hausnummer].filter(Boolean).join(' ');
      const ortZeile = [p.plz, p.ort].filter(Boolean).join(' ');
      const anschrift = [strasseZeile, ortZeile].filter(Boolean).join('\n');
      const snapshot = { name, anschrift, telefon: p.telefon || '', email: p.email || '' };
      setEntwurfSnapshot(snapshot);
      updateStelle(stelle.id, {
        absender_name: snapshot.name,
        absender_anschrift: snapshot.anschrift,
        absender_telefon: snapshot.telefon,
        absender_email: snapshot.email,
      }).catch(() => {});
    }).catch(() => {});
    return () => { abgebrochen = true; };
  }, [istLebenslaufEntwurf, stelle?.id, stelle?.absender_name, entwurfProfilName]);

  // Eine ganze Seite soll auf einen Blick zu sehen sein, ohne zu scrollen — genauso wie
  // beim Anschreiben.
  //
  // Vorher richtete sich die Größe allein nach der Breite der Spalte. Auf einem flachen
  // Laptop-Fenster hieß das: Die Seite war zwar breit genug eingepasst, ihre untere
  // Hälfte lag aber unter der Fensterkante. Genau das hat der User am 2026-08-11
  // beanstandet — beim Anschreiben passe alles, beim Lebenslauf müsse er scrollen.
  // Der Grund war, dass hier nach der Breite und dort nach der Höhe gerechnet wurde.
  //
  // Jetzt entscheidet der kleinere von beiden Werten: Was in die Breite passt UND was in
  // die Höhe passt. Über 100 % geht es nicht hinaus, sonst wäre die Vorschau größer als
  // das gedruckte Papier. Unter 45 % auch nicht — darunter ist nichts mehr zu lesen, und
  // eine unlesbare Seite, die ganz ins Bild passt, hilft niemandem.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let raf = 0;
    let letzterScale = null;
    const update = () => {
      const breitenFit = (el.clientWidth - CV_RAND_X) / CV_SEITE_BREITE;

      // Bezugspunkt für die Höhe ist die Oberkante des Ergebnis-Blocks, nicht der Anfang
      // des scrollbaren Inhalts: Der Block wird beim Anzeigen an die Oberkante der Insel
      // geschoben (siehe StellenEditor.js), Stellenbeschreibung und Fit-Analyse wandern
      // dabei aus dem Bild. Ihre Höhe darf deshalb nicht vom Platz abgezogen werden.
      // Beide Kanten bewegen sich beim Scrollen gemeinsam — die Zahl bleibt stabil und
      // die Seite zappelt nicht beim Scrollen in der Größe.
      const roller = el.closest('.se-main-scroll');
      let hoehenFit = Infinity;
      if (roller) {
        const stil = getComputedStyle(roller);
        const innen = roller.clientHeight
          - (parseFloat(stil.paddingTop)    || 0)
          - (parseFloat(stil.paddingBottom) || 0);
        const bezug = el.closest('.se-ergebnis') || roller;
        const obenDrueber = el.getBoundingClientRect().top - bezug.getBoundingClientRect().top;
        hoehenFit = (innen - obenDrueber - CV_LUFT) / CV_SEITE_HOEHE;
      }

      const fit = Math.max(CV_SKALA_MIN, Math.min(1, breitenFit, hoehenFit));
      // Der eingepasste Wert wird immer gemeldet, auch wenn gerade der User zoomt: Der
      // Knopf „Anpassen" im Zoom-Regler muss wissen, wohin er zurückstellt.
      onFit?.(fit);

      // Hat der User selbst gezoomt, gilt seine Zahl. Die Einpassung rechnet weiter mit,
      // überschreibt sie aber nicht mehr — sonst spränge das Blatt bei jeder
      // Fenster-Änderung aus seiner Größe zurück.
      const scale = zoom ?? fit;
      if (scale === letzterScale) return; // gleicher Wert → kein erneutes Durchrechnen
      letzterScale = scale;
      el.style.setProperty('--cv-page-scale', scale);
    };
    update();
    // rAF defers the write out of the observer callback → breaks the
    // "ResizeObserver loop completed with undelivered notifications" feedback loop
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [zoom, onFit]); // observer handles size changes; neu aufgesetzt nur beim Zoomen

  const effectiveNutzer = useMemo(() => {
    if (istLebenslaufEntwurf) {
      // Eingefrorene Kopie statt Live-Profil — spätere Profil-Änderungen dürfen
      // einen schon angelegten Entwurf nicht mehr verändern.
      if (stelle?.absender_name) {
        return {
          name: stelle.absender_name,
          anschrift: stelle.absender_anschrift,
          telefon: stelle.absender_telefon,
          email: stelle.absender_email,
        };
      }
      return entwurfSnapshot;
    }
    if (nutzer) {
      return {
        ...nutzer,
        telefon: nutzer.telefon || stelle?.absender_telefon,
        email: nutzer.email || stelle?.absender_email,
        anschrift: (nutzer.strasse || nutzer.plz) ? undefined : stelle?.absender_anschrift,
      };
    }
    if (stelle?.absender_name) {
      return {
        name: stelle.absender_name,
        anschrift: stelle.absender_anschrift,
        telefon: stelle.absender_telefon,
        email: stelle.absender_email,
      };
    }
    return null;
  }, [nutzer, stelle, istLebenslaufEntwurf, entwurfSnapshot]);

  const hasCvData = !!(stelle?.cv_data || stelle?.cv_rohtext);

  useLayoutEffect(() => {
    if (!measureRef.current || !hasCvData) return;
    setPages(null);
    const { sektionen: parsedSektionen } = parseCvSource(stelle);
    const hidden = new Set(dokumentOptionen?.cv?.hidden_section_keys || []);
    const sichtbareSektionen = parsedSektionen
      .map((s, originalIdx) => ({ ...s, _originalIndex: originalIdx, _sectionKey: sektionKey(s) }))
      .filter((s) => !hidden.has(s._sectionKey));
    const tid = inferTemplateId(stelle, sichtbareSektionen);
    setTemplateId(tid);
    const verteilung = berechneSeitenverteilung(measureRef.current, sichtbareSektionen, tid);
    setPages(verteilung);
  }, [stelle, hasCvData, dokumentOptionen]);

  if (loading) {
    const sichtbareKeywords = (keywords || []).slice(0, 10);
    const line = (cls = '') => <span className={`se-cv-skel-line ${cls}`} />;
    return (
      <div className="cv-vorschau-wrapper se-cv-build">
        <div className="cv-vorschau-label">
          {(onUndo || onRedo) && (
            <div className="cv-history-btns">
              <button className="cv-history-btn" disabled title="Rückgängig"><UndoIcon /></button>
              <button className="cv-history-btn" disabled title="Wiederherstellen"><RedoIcon /></button>
            </div>
          )}
          {tabsElement || <span>Lebenslauf Vorschau</span>}
        </div>
        <div className="cv-paginas-container se-cv-skeleton-container">
          <div className="cv-pagina se-cv-skeleton-page" aria-label="Lebenslauf wird generiert">
            <div className="se-cv-skel-header">
              <div>{line('se-cv-skel-name')}{line('se-cv-skel-contact')}</div>
              <span className="se-cv-skel-photo" />
            </div>
            <div className="se-cv-skel-section se-cv-skel-profile">
              {line('se-cv-skel-title')}{line()}{line('short')}
            </div>
            <div className="se-cv-skel-grid">
              <div className="se-cv-skel-main">
                {[0, 1, 2].map((i) => (
                  <div className="se-cv-skel-section" key={i}>
                    {line('se-cv-skel-title')}{line()}{line('medium')}{line('long')}
                  </div>
                ))}
              </div>
              <div className="se-cv-skel-side">
                {[0, 1, 2, 3].map((i) => (
                  <div className="se-cv-skel-section compact" key={i}>
                    {line('se-cv-skel-title')}{line('short')}{line('medium')}
                  </div>
                ))}
              </div>
            </div>
            {sichtbareKeywords.length > 0 && (
              <div className="se-cv-keyword-layer" aria-hidden="true">
                {sichtbareKeywords.map((kw, i) => {
                  const pos = CV_SKEL_KW_POSITIONS[i % CV_SKEL_KW_POSITIONS.length];
                  return (
                    <span key={`${kw}-${i}`} className="se-cv-flying-keyword"
                      style={{ '--kw-x': `${pos.x}%`, '--kw-y': `${pos.y}%`, '--kw-delay': pos.d }}>
                      {kw}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (istLeererEntwurf && !hasCvData) {
    const leerTemplateId = inferTemplateId(stelle, []);
    const { Kopf: KopfLeer } = getTemplate(leerTemplateId);
    return (
      <div className="cv-vorschau-wrapper">
        <div className="cv-vorschau-label">
          {tabsElement || <span>Lebenslauf Vorschau</span>}
        </div>
        <div className="cv-paginas-container">
          {bearbeitenButton}
          <div className={`cv-pagina${leerTemplateId === 'professionell' ? ' cv-pagina--professionell' : ''}`}>
            <KopfLeer profil="" nutzer={effectiveNutzer} fotoUrl={stelle?.foto_url || null} onFotoClick={null} markierungen={[]} />
          </div>
        </div>
      </div>
    );
  }

  if (!hasCvData) return null;

  const {
    profil: profilText,
    sektionen: parsedSektionen,
    keywordMarkierungen,
  } = parseCvSource(stelle);
  const alleMarkierungen = normalisiereCvMarkierungen(keywordMarkierungen);
  const cvMarkierungen = highlightingAktiv ? alleMarkierungen : [];
  const hidden = new Set(dokumentOptionen?.cv?.hidden_section_keys || []);
  const sektionen = parsedSektionen
    .map((s, originalIdx) => ({ ...s, _originalIndex: originalIdx, _sectionKey: sektionKey(s) }))
    .filter((s) => !hidden.has(s._sectionKey));
  const { Kopf, Sektion, Footer } = getTemplate(inferTemplateId(stelle, sektionen));

  const fallback = [{
    hatKopf: true,
    teile: sektionen.map((s, i) => ({ sektionIdx: i, zeigeTitel: true, vonEintrag: 0, bisEintrag: s.eintraege?.length ?? s.bullets?.length ?? 0 })),
    verbleibend: 0,
  }];
  const paginiert = (pages && pages[0]?.teile &&
    pages.every(p => p.teile.every(t => t.sektionIdx < sektionen.length)))
    ? pages : fallback;
  // ponytail: Während des Ziehens auf EIN durchgehendes Blatt (ganze Sektionen, keine festen
  // Seitenkanten) umschalten — so wird nichts abgeschnitten und das Verschieben über die
  // Seitengrenze rechnet sauber. Nach dem Loslassen automatisch zurück in die Seitenansicht.
  const seitenLayout = aktiveDragId ? fallback : paginiert;


  const onDragStart = ({ active }) => setAktiveDragId(active.id);

  const onDragEnd = ({ active, over }) => {
    setAktiveDragId(null);
    if (!over || active.id === over.id) return;

    const activeKey = active.id.replace('s-', '');
    const overKey = over.id.replace('s-', '');
    const oldIdx = parsedSektionen.findIndex((s) => sektionKey(s) === activeKey);
    const newIdx = parsedSektionen.findIndex((s) => sektionKey(s) === overKey);
    if (oldIdx < 0 || newIdx < 0) return;

    const neueSektionen = [...parsedSektionen];
    const [moved] = neueSektionen.splice(oldIdx, 1);
    neueSektionen.splice(oldIdx < newIdx ? newIdx - 1 : newIdx, 0, moved);

    if (stelle.cv_data) {
      const neuesCvData = {
        ...stelle.cv_data,
        sektionen: neueSektionen.map((s) => {
          const { inhaltRaw: _, _originalIndex, _sectionKey, ...rest } = s;
          return rest;
        }),
      };
      if (onRebuildDone) onRebuildDone(neuesCvData);
      rebuildDokument(stelle.id, neueSektionen.map((s) => s.name)).catch(() => {});
    } else {
      const headerPart = (stelle.cv_rohtext || '').split('\n## ')[0].trim();
      const body = neueSektionen.map((s) => `## ${s.name}\n${s.inhaltRaw}`).join('\n');
      const neuerRohtext = headerPart ? `${headerPart}\n${body}` : body;
      if (onRebuildDone) onRebuildDone(neuerRohtext);
      rebuildDokument(stelle.id, neueSektionen.map((s) => s.name)).catch(() => {});
    }
  };

  const handleFotoGewaehlt = ({ type, url, file }) => {
    setFotoPickerOffen(false);
    setCropperBild(url);
    setCropperNeuesFile(type === 'neu' ? file : null);
  };

  const handleCropFertig = async ({ url, storagePath }) => {
    if (cropperBild?.startsWith('blob:')) URL.revokeObjectURL(cropperBild);
    setCropperBild(null);
    setCropperNeuesFile(null);
    await updateStelle(stelle.id, { foto_url: url, foto_storage_path: storagePath }).catch(() => {});
    if (onFotoChange) onFotoChange(url);
  };

  const handleFotoEntfernen = async () => {
    await updateStelle(stelle.id, { foto_url: null, foto_storage_path: null }).catch(() => {});
    if (onFotoChange) onFotoChange(null);
  };

  return (
    <div className="cv-vorschau-wrapper">
      <div className="cv-vorschau-label">
        {(onUndo || onRedo) && (
          <div className="cv-history-btns">
            <button
              className="cv-history-btn"
              onClick={onUndo}
              disabled={!canUndo}
              title="Rückgängig"
            >
              <UndoIcon />
              {undoCount > 0 && <span className="cv-history-count">{undoCount}</span>}
            </button>
            <button
              className="cv-history-btn"
              onClick={onRedo}
              disabled={!canRedo}
              title="Wiederherstellen"
            >
              <RedoIcon />
              {redoCount > 0 && <span className="cv-history-count">{redoCount}</span>}
            </button>
          </div>
        )}
        {tabsElement || <span>Lebenslauf Vorschau</span>}
        <div className="cv-vorschau-label-actions">
          {alleMarkierungen.length > 0 && (
            <button
              className={`cv-ats-toggle${highlightingAktiv ? ' cv-ats-toggle--aktiv' : ''}`}
              onClick={() => setHighlightingAktiv((v) => !v)}
              title={highlightingAktiv ? 'ATS-Hervorhebungen ausblenden' : 'ATS-Hervorhebungen anzeigen'}
              type="button"
            >
              <span className="cv-ats-toggle-dot" />
              ATS
            </button>
          )}
        </div>
      </div>

      {onRebuildDone && stelle?.foto_url && (
        <div className="cv-foto-feld">
          <button
            className="cv-foto-feld-btn cv-foto-feld-btn--aktiv"
            type="button"
            onClick={() => setFotoPickerOffen(true)}
          >
            <img src={stelle.foto_url} alt="" className="cv-foto-feld-thumb" />
            <span className="cv-foto-feld-text">
              <strong>Foto ändern</strong>
              <span>Wird in der Standard-Vorlage rechts platziert</span>
            </span>
          </button>
          <button className="cv-foto-feld-remove" type="button" onClick={handleFotoEntfernen}>
            Entfernen
          </button>
        </div>
      )}

      {/* Versteckter Mess-Container */}
      <div ref={measureRef} className={`cv-measure-hidden${templateId === 'professionell' ? ' cv-measure-hidden--professionell' : ''}`}>
        <div data-role="kopf">
          <Kopf profil={profilText} nutzer={effectiveNutzer} fotoUrl={stelle?.foto_url} onFotoClick={null} markierungen={cvMarkierungen} />
        </div>
        {sektionen.map((s, i) => (
          <div key={i} data-sektion={i}>
            <Sektion s={s} sektionIdx={i} markierungen={cvMarkierungen} fotoUrl={i === 0 ? (stelle?.foto_url || null) : null} />
          </div>
        ))}
      </div>

      {/* Sichtbare Seiten */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} measuring={{ droppable: { strategy: MeasuringStrategy.Always } }} autoScroll={{ threshold: { x: 0, y: 0.35 }, acceleration: 18 }} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setAktiveDragId(null)}>
        <SortableContext items={sektionen.map((s) => `s-${s._sectionKey}`)} strategy={verticalListSortingStrategy}>
          <div className={`cv-paginas-container${aktiveDragId ? ' is-dragging-aktiv' : ''}`} ref={containerRef}>
            {bearbeitenButton}
            {seitenLayout.map((page, pi) => (
              <div key={pi} className={`cv-pagina${templateId === 'professionell' ? ' cv-pagina--professionell' : ''}`}>
                {page.hatKopf && (
                  <Kopf
                    profil={profilText}
                    nutzer={effectiveNutzer}
                    fotoUrl={stelle?.foto_url || null}
                    onFotoClick={onRebuildDone ? () => setFotoPickerOffen(true) : null}
                    markierungen={cvMarkierungen}
                    onProfilClick={bearbeitungsmodus && onProfilClick ? () => onProfilClick(profilText) : null}
                  />
                )}
                {page.teile.map((teil, ti) => {
                  const s = sektionen[teil.sektionIdx];
                  if (!s) return null; // guard against stale page layout during stelle switch
                  // Rubrik-Knöpfe + gelbes Hervorheben nur im Bearbeitungsmodus.
                  const showDrag = teil.zeigeTitel && !!onRebuildDone && bearbeitungsmodus;
                  const sectionFotoUrl = pi === 0 && ti === 0 ? (stelle?.foto_url || null) : null;
                  // Skelett-Platzhalter nur im gerade bearbeiteten Abschnitt (leere Felder als graue Kästchen).
                  const zeigeSkelett = editSektionIdx != null
                    && (s._originalIndex ?? teil.sektionIdx) === editSektionIdx;

                  if (showDrag) {
                    return (
                      <SortableSektionWrapper
                        key={`s-${s._sectionKey}`}
                        sId={`s-${s._sectionKey}`}
                        isMobile={isMobile}
                        onEditClick={() => onEditToggle && onEditToggle(s._originalIndex ?? teil.sektionIdx, s)}
                      >
                        <Sektion
                          s={s}
                          sektionIdx={teil.sektionIdx}
                          zeigeTitel={teil.zeigeTitel}
                          vonEintrag={teil.vonEintrag}
                          bisEintrag={teil.bisEintrag}
                          markierungen={cvMarkierungen}
                          fotoUrl={sectionFotoUrl}
                          skeleton={zeigeSkelett}
                        />
                      </SortableSektionWrapper>
                    );
                  }

                  return (
                    <Sektion
                      key={`${pi}-${ti}`}
                      s={s}
                      sektionIdx={teil.sektionIdx}
                      zeigeTitel={teil.zeigeTitel}
                      vonEintrag={teil.vonEintrag}
                      bisEintrag={teil.bisEintrag}
                      markierungen={cvMarkierungen}
                      fotoUrl={sectionFotoUrl}
                      skeleton={zeigeSkelett}
                    />
                  );
                })}
                <Footer nutzer={effectiveNutzer} />
              </div>
            ))}
          </div>
        </SortableContext>
        {/* ponytail: unsichtbares Overlay hält dnd-kit im "useDragOverlay"-Modus, damit die
            Quell-Sektion als graue Vorschau an der Landeposition bleibt statt am Cursor zu kleben.
            Muss gerendert (= messbar) sein; nur visibility:hidden, niemals display:none. */}
        <DragOverlay>
          {(() => {
            if (!aktiveDragId) return null;
            const s = sektionen.find((x) => `s-${x._sectionKey}` === aktiveDragId);
            if (!s) return null;
            return (
              <div className="cv-sektion-sortable cv-sektion-drag-overlay">
                <Sektion s={s} sektionIdx={s._originalIndex} zeigeTitel markierungen={cvMarkierungen} />
              </div>
            );
          })()}
        </DragOverlay>
      </DndContext>

      {fotoPickerOffen && (
        <FotoPickerModal
          onFotoGewaehlt={handleFotoGewaehlt}
          onSchliessen={() => setFotoPickerOffen(false)}
        />
      )}
      {cropperBild && (
        <FotoCropperModal
          bildUrl={cropperBild}
          neuesFile={cropperNeuesFile}
          onFertig={handleCropFertig}
          onAbbrechen={() => { if (cropperBild?.startsWith('blob:')) URL.revokeObjectURL(cropperBild); setCropperBild(null); setCropperNeuesFile(null); }}
        />
      )}
    </div>
  );
});

export default CVVorschau;
